import { Component, Input } from '@angular/core';
import { RevenuePeriod } from '@vendasta/sales-orders';
import { BehaviorSubject } from 'rxjs';
import { SummaryItem, TaxOption, UILineItem } from '../../order-form/order-item-list/interface';
import { salesOrderPeriodToBillingFrequency } from '../../shared/conversion-utils';
import { OrderSummaryService } from '../order-summary.service';
import { Frequency } from '@vendasta/galaxy/frequency';

interface OrderCharges {
  charges: Map<string, number>;
  currency: string;
}

const onceFrequency = salesOrderPeriodToBillingFrequency(RevenuePeriod.ONETIME).toUpperCase();

@Component({
  selector: 'store-order-summarized-charges',
  templateUrl: './order-summarized-charges.component.html',
  styleUrls: ['./order-summarized-charges.component.scss'],
  standalone: false,
})
export class OrderSummarizedChargesComponent {
  @Input()
  set orderItems(orderItems: UILineItem[]) {
    const summaryItems = OrderSummaryService.getPriceSummary(orderItems);
    this._orderItems = summaryItems.items;
    this.orderCharges$$.next(this.calculateOrderCharges(this._orderItems, this._taxOptions));
  }

  @Input()
  set taxOptions(taxOptions: TaxOption[]) {
    this._taxOptions = taxOptions;
    this.orderCharges$$.next(this.calculateOrderCharges(this._orderItems, this._taxOptions));
  }

  @Input() expanded: boolean;

  private _orderItems: SummaryItem[];
  private _taxOptions: TaxOption[];

  private orderCharges$$ = new BehaviorSubject<OrderCharges>(null);
  public orderCharges$ = this.orderCharges$$.asObservable();

  private calculateOrderCharges(orderItems: SummaryItem[], taxOptions: TaxOption[]): OrderCharges {
    const orderCharges: Map<string, number> = new Map();
    (orderItems || []).forEach((orderItem) => {
      this.addChargeWithApplicableTaxes(orderItem, taxOptions, orderCharges);
    });
    this.convertChargesToDollars(orderCharges);

    return {
      charges: orderCharges,
      currency: orderItems.length > 0 ? orderItems[0].currencyCode : null,
    };
  }

  private addChargeWithApplicableTaxes(
    orderItem: SummaryItem,
    taxOptions: TaxOption[],
    orderCharges: Map<string, number>,
  ): void {
    (orderItem.revenueComponents || []).forEach(({ period, value }) => {
      const formattedFrequency = salesOrderPeriodToBillingFrequency(period).toUpperCase();
      if (formattedFrequency !== onceFrequency) {
        const frequencyTotal = orderCharges.get(formattedFrequency) || 0;
        if (frequencyTotal + value > 0) {
          const taxTotal = taxOptions
            ?.filter(({ applicableSkus }) => applicableSkus?.includes(orderItem.billingId))
            .reduce((total, { percentageMultiplier }) => total + value * percentageMultiplier, 0);
          orderCharges.set(formattedFrequency, frequencyTotal + value + taxTotal);
        }
      }
    });
  }

  private convertChargesToDollars(orderCharges: Map<string, number>): void {
    orderCharges.forEach((frequencyTotal, frequencyKey) => {
      orderCharges.set(frequencyKey, frequencyTotal / 100);
    });
  }

  protected readonly Frequency = Frequency;
}
